import { useState, useEffect } from 'react';
import { getTaxSettings, updateTaxSettings, getAllDeclarations, verifyDeclaration, downloadForm16 } from '../../lib/taxApi';
import { systemAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Loader2, ShieldCheck, CheckSquare, XSquare, Eye, Download, AlertCircle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { formatIndianCurrency, cn } from '../../lib/utils';
import { Checkbox } from '../../components/ui/checkbox';
import ModuleDisabled from '../../components/shared/ModuleDisabled';

const TaxManagement = () => {
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState({
        current_financial_year: '2025-26'
    });
    const [error, setError] = useState(null);
    const [systemConfig, setSystemConfig] = useState(null);
    const [declarations, setDeclarations] = useState([]);
    const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
    const [selectedDecl, setSelectedDecl] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBulkLoading, setIsBulkLoading] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [taxSettings, sysResponse] = await Promise.all([
                getTaxSettings(),
                systemAPI.getSettings()
            ]);
            setSettings(taxSettings);
            setSystemConfig(sysResponse.data);

            const decls = await getAllDeclarations(taxSettings.current_financial_year);
            setDeclarations(decls);
        } catch (error) {
            if (error.response?.status === 403) {
                setError(error.response.data.detail);
            } else {
                toast.error('Failed to fetch tax management data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateSettings = async () => {
        setIsUpdatingSettings(true);
        try {
            await updateTaxSettings({
                open_window: settings.investment_declaration_open,
                financial_year: settings.current_financial_year
            });
            toast.success('Tax settings updated successfully');
        } catch (error) {
            toast.error('Failed to update tax settings');
        } finally {
            setIsUpdatingSettings(false);
        }
    };

    const handleVerify = async (status) => {
        try {
            await verifyDeclaration(selectedDecl.id, { status, remarks });
            toast.success(`Declaration ${status.toLowerCase()} successfully`);
            setIsViewModalOpen(false);
            setSelectedDecl(null);
            setRemarks('');
            fetchData();
        } catch (error) {
            toast.error('Failed to verify declaration');
        }
    };

    const handleBulkVerify = async (status) => {
        if (!selectedIds.length) return;
        
        setIsBulkLoading(true);
        let successCount = 0;
        let failCount = 0;

        try {
            await Promise.all(selectedIds.map(async (id) => {
                try {
                    await verifyDeclaration(id, { status, remarks: `Bulk ${status.toLowerCase()}` });
                    successCount++;
                } catch (err) {
                    failCount++;
                }
            }));
            
            if (successCount > 0) {
                toast.success(`${successCount} declarations ${status.toLowerCase()} successfully`);
            }
            if (failCount > 0) {
                toast.error(`Failed to verify ${failCount} declarations`);
            }
            
            setSelectedIds([]);
            fetchData();
        } catch (error) {
            toast.error('Bulk verification encountered an error');
        } finally {
            setIsBulkLoading(false);
        }
    };

    const toggleSelectAll = (tabDecls) => {
        if (selectedIds.length === tabDecls.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(tabDecls.map(d => d.id));
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(i => i !== id)
                : [...prev, id]
        );
    };

    const getFilteredDeclarations = (status) => {
        return declarations.filter(d => status === 'PENDING' ? d.status === 'SUBMITTED' : d.status === status);
    };

    if (loading && !declarations.length) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    if (error) {
        return <ModuleDisabled moduleName="tax" />;
    }

    return (
        <div className="space-y-6 animate-slide-up">
            <div>
                <h1 className="text-3xl font-bold tracking-tight">Tax Management</h1>
                <p className="text-muted-foreground">Manage employee tax declarations and settings</p>

            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <Card className="md:col-span-1 lg:col-span-1 border-primary/20 bg-primary/5">
                    <CardHeader>
                        <CardTitle className="text-lg">Admin Controls</CardTitle>
                        <CardDescription>Declaration window and FY settings</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="flex items-center justify-between space-x-2">
                            <div className="space-y-0.5">
                                <Label htmlFor="window-toggle">Investment Declaration</Label>
                                <p className="text-xs text-muted-foreground">Open/Close window for employees</p>
                            </div>
                            <Switch
                                id="window-toggle"
                                checked={settings.investment_declaration_open}
                                onCheckedChange={(val) => setSettings(prev => ({ ...prev, investment_declaration_open: val }))}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="fy">Active Financial Year</Label>
                            <Input
                                id="fy"
                                value={settings.current_financial_year}
                                onChange={(e) => setSettings(prev => ({ ...prev, current_financial_year: e.target.value }))}
                                placeholder="e.g. 2024-25"
                            />
                        </div>

                        <Button
                            className="w-full"
                            onClick={handleUpdateSettings}
                            disabled={isUpdatingSettings}
                        >
                            {isUpdatingSettings && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Save Configuration
                        </Button>
                    </CardContent>
                </Card>

                <Card className="md:col-span-1 lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="text-lg">Summary</CardTitle>
                        <CardDescription>Overview of declarations for FY {settings.current_financial_year}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div className="p-4 rounded-lg bg-indigo-50 border border-indigo-100">
                                <p className="text-xs text-indigo-600 font-medium uppercase">Participation</p>
                                <p className="text-2xl font-bold text-indigo-900">
                                    {declarations.length} / {settings.total_employees || 0}
                                </p>
                                <p className="text-[10px] text-indigo-400 font-medium">Declared / Total Active</p>
                            </div>
                            <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
                                <p className="text-xs text-amber-600 font-medium uppercase">Pending</p>
                                <p className="text-2xl font-bold text-amber-900">
                                    {declarations.filter(d => d.status === 'SUBMITTED').length}
                                </p>
                                <p className="text-[10px] text-amber-400 font-medium">Awaiting Verification</p>
                            </div>
                            <div className="p-4 rounded-lg bg-green-50 border border-green-100">
                                <p className="text-xs text-green-600 font-medium uppercase">Approved</p>
                                <p className="text-2xl font-bold text-green-900">
                                    {declarations.filter(d => d.status === 'APPROVED').length}
                                </p>
                                <p className="text-[10px] text-green-400 font-medium">Finalized Records</p>
                            </div>
                            <div className="p-4 rounded-lg bg-red-50 border border-red-100">
                                <p className="text-xs text-red-600 font-medium uppercase">Rejected</p>
                                <p className="text-2xl font-bold text-red-900">
                                    {declarations.filter(d => d.status === 'REJECTED').length}
                                </p>
                                <p className="text-[10px] text-red-400 font-medium">Action Required</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <Tabs defaultValue="pending" className="w-full" onValueChange={() => setSelectedIds([])}>
                    <CardHeader className="flex flex-row items-baseline justify-between border-b pb-0 space-y-0">
                        <div className="flex items-center gap-6">
                            <CardTitle className="pb-4">Employee Declarations</CardTitle>
                            <TabsList className="bg-transparent gap-6 h-auto p-0 mb-[-1px]">
                                <TabsTrigger 
                                    value="pending" 
                                    className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-4 h-auto font-semibold"
                                >
                                    Pending ({getFilteredDeclarations('PENDING').length})
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="approved" 
                                    className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-4 h-auto font-semibold"
                                >
                                    Approved ({getFilteredDeclarations('APPROVED').length})
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="rejected" 
                                    className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-4 h-auto font-semibold"
                                >
                                    Rejected ({getFilteredDeclarations('REJECTED').length})
                                </TabsTrigger>
                            </TabsList>
                        </div>
                        <div className="pb-4 h-[40px] flex items-center">
                            {selectedIds.length > 0 && (
                                <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-4">
                                    <span className="text-sm font-medium text-slate-500 mr-2">
                                        {selectedIds.length} Selected
                                    </span>
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="text-red-600 border-red-200 hover:bg-red-50"
                                        onClick={() => handleBulkVerify('REJECTED')}
                                        disabled={isBulkLoading}
                                    >
                                        <XSquare className="h-4 w-4 mr-1" /> Bulk Reject
                                    </Button>
                                    <Button 
                                        size="sm" 
                                        className="bg-green-600 hover:bg-green-700"
                                        onClick={() => handleBulkVerify('APPROVED')}
                                        disabled={isBulkLoading}
                                    >
                                        {isBulkLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckSquare className="h-4 w-4 mr-1" />}
                                        Bulk Approve
                                    </Button>
                                </div>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {['pending', 'approved', 'rejected'].map((tabStatus) => (
                            <TabsContent key={tabStatus} value={tabStatus} className="mt-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="hover:bg-transparent bg-slate-50/50">
                                            {tabStatus === 'pending' && (
                                                <TableHead className="w-[50px] pl-6">
                                                    <Checkbox 
                                                        checked={selectedIds.length === getFilteredDeclarations('PENDING').length && getFilteredDeclarations('PENDING').length > 0}
                                                        onCheckedChange={() => toggleSelectAll(getFilteredDeclarations('PENDING'))}
                                                    />
                                                </TableHead>
                                            )}
                                            <TableHead className={cn(tabStatus !== 'pending' && "pl-6")}>Employee</TableHead>
                                            <TableHead>Regime</TableHead>
                                            <TableHead>Total Declaration</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Last Updated</TableHead>
                                            <TableHead className="text-right pr-6">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {getFilteredDeclarations(tabStatus === 'pending' ? 'PENDING' : tabStatus.toUpperCase()).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={tabStatus === 'pending' ? 7 : 6} className="text-center py-20 text-muted-foreground">
                                                    <div className="flex flex-col items-center gap-2">
                                                        <AlertCircle className="h-8 w-8 text-slate-300" />
                                                        <p>No {tabStatus} declarations found</p>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            getFilteredDeclarations(tabStatus === 'pending' ? 'PENDING' : tabStatus.toUpperCase()).map((decl) => (
                                                <TableRow key={decl.id} className={cn(selectedIds.includes(decl.id) && "bg-amber-50/30 hover:bg-amber-50/50")}>
                                                    {tabStatus === 'pending' && (
                                                        <TableCell className="pl-6">
                                                            <Checkbox 
                                                                checked={selectedIds.includes(decl.id)}
                                                                onCheckedChange={() => toggleSelect(decl.id)}
                                                            />
                                                        </TableCell>
                                                    )}
                                                    <TableCell className={cn(tabStatus !== 'pending' && "pl-6")}>
                                                        <div className="font-medium">{decl.employee_name || 'Unknown Employee'}</div>
                                                        <div className="text-xs text-muted-foreground font-mono">{decl.employee_code || decl.employee_id.slice(0, 8)}</div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant={decl.regime === 'NEW' ? 'secondary' : 'outline'}>
                                                            {decl.regime}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {decl.regime === 'OLD' ? (
                                                            <span className="font-semibold text-indigo-600">
                                                                {formatIndianCurrency(decl.investments.reduce((sum, i) => sum + i.amount, 0))}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted-foreground italic">Not Applicable</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge
                                                            variant={decl.status === 'APPROVED' ? 'default' : decl.status === 'REJECTED' ? 'destructive' : 'secondary'}
                                                        >
                                                            {decl.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>{new Date(decl.updated_at).toLocaleDateString()}</TableCell>
                                                    <TableCell className="text-right pr-6">
                                                        <div className="flex justify-end gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setSelectedDecl(decl);
                                                                    setIsViewModalOpen(true);
                                                                }}
                                                            >
                                                                <Eye className="h-4 w-4 mr-1" /> {tabStatus === 'pending' ? 'Verify' : 'View'}
                                                            </Button>
                                                            {systemConfig?.form16_download_enabled && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={async (e) => {
                                                                        e.preventDefault();
                                                                        try {
                                                                            const blob = await downloadForm16(settings.current_financial_year, decl.employee_id);
                                                                            const url = window.URL.createObjectURL(new Blob([blob]));
                                                                            const link = document.createElement('a');
                                                                            link.href = url;
                                                                            link.setAttribute('download', `Form16_${decl.employee_code || decl.employee_id.slice(0, 8)}_${settings.current_financial_year}.pdf`);
                                                                            document.body.appendChild(link);
                                                                            link.click();
                                                                            link.remove();
                                                                            toast.success('Form 16 generated');
                                                                        } catch (error) {
                                                                            toast.error('Failed to generate Form 16');
                                                                        }
                                                                    }}
                                                                >
                                                                    <Download className="h-4 w-4 mr-1" /> Form 16
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TabsContent>
                        ))}
                    </CardContent>
                </Tabs>
            </Card>

            {/* View/Verification Modal */}
            <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Verify Investment Declaration</DialogTitle>
                        <DialogDescription>
                            Review the details submitted by the employee
                        </DialogDescription>
                    </DialogHeader>

                    {selectedDecl && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-slate-50 border border-slate-200">
                                <div>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Employee</p>
                                    <p className="font-bold text-lg">{selectedDecl.employee_name}</p>
                                    <p className="text-sm font-mono text-muted-foreground">{selectedDecl.employee_code || selectedDecl.employee_id}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Tax Regime</p>
                                    <Badge variant={selectedDecl.regime === 'NEW' ? 'secondary' : 'outline'} className="text-sm">
                                        {selectedDecl.regime}
                                    </Badge>
                                </div>
                            </div>

                            {selectedDecl.regime === 'OLD' ? (
                                <div className="max-h-[300px] overflow-y-auto border rounded-md">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Category</TableHead>
                                                <TableHead>Investment</TableHead>
                                                <TableHead className="text-right">Amount</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {selectedDecl.investments.map((inv, i) => (
                                                <TableRow key={i}>
                                                    <TableCell><Badge variant="outline">{inv.category}</Badge></TableCell>
                                                    <TableCell>{inv.name}</TableCell>
                                                    <TableCell className="text-right font-semibold">{formatIndianCurrency(inv.amount)}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow className="bg-slate-50">
                                                <TableCell colSpan={2} className="font-bold">Total Declaration</TableCell>
                                                <TableCell className="text-right font-bold text-lg">
                                                    {formatIndianCurrency(selectedDecl.investments.reduce((sum, i) => sum + i.amount, 0))}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="p-8 text-center bg-slate-50 rounded-lg border border-dashed border-slate-300">
                                    <ShieldCheck className="h-10 w-10 text-indigo-400 mx-auto mb-2" />
                                    <p className="font-medium text-slate-600">New Regime Selected</p>
                                    <p className="text-xs text-slate-400">Declarations are not required for the New Tax Regime.</p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="remarks">Admin Remarks</Label>
                                <Input
                                    id="remarks"
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    placeholder="Reason for approval/rejection"
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleVerify('REJECTED')}
                        >
                            <XSquare className="h-4 w-4 mr-2" /> Reject
                        </Button>
                        <Button
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleVerify('APPROVED')}
                        >
                            <CheckSquare className="h-4 w-4 mr-2" /> Approve
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TaxManagement;
